import {createContext, ReactNode, useCallback, useContext, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useHistory, useLocation, useRouteMatch} from "react-router-dom";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";
import {flexAlignCenter, onDesktop, p, zIndex} from "@pg-design/helpers-css";
import {CloseIcon, InfoIcon} from "@pg-design/icons";
import {Text} from "@pg-design/text";

import {dataLayer} from "../../../../utils/data_layer";
import {useAppDispatch} from "../../../../utils/hooks/store_hooks";
import {useConditionalPlatformRoute} from "../../../../utils/hooks/useConditionalPlatformRoute";
import {chartsRoutes} from "../../common/app/routing/charts_routes";
import {getMenuItems} from "../../common/app/routing/menu_items";
import {breakpoints} from "../../common/app/styles/breakpoints";
import {useEffectiveCitySlug} from "../../features/analytical_platform/hooks/use_effective_city_slug";
import {toggleOnboarding} from "../../features/analytical_platform/redux/onboarding_slice";
import {getSideMenuIcon} from "./side_menu_icons";

const getHomepagePaths = (lang: string) => [
    chartsRoutes[lang].properties.root,
    chartsRoutes[lang].flats.root,
    chartsRoutes[lang].houses.root
];

const usePlatformMenuNavigation = () => {
    const {path} = useRouteMatch();
    const citySlug = useEffectiveCitySlug();
    const {pathname} = useLocation();
    const {i18n} = useTranslation();

    const menuItems = useMemo(() => getMenuItems(citySlug, pathname, i18n), [citySlug, pathname, i18n]);

    const isLinkActive = useCallback(
        (targetPath: string, index: number) => {
            if (!targetPath) {
                return false;
            }
            if (getHomepagePaths(i18n.language).includes(path) && index === 0) {
                return true;
            }
            if (targetPath === chartsRoutes[i18n.language].flats.fltr) {
                return pathname === targetPath || pathname.startsWith(`${targetPath}`);
            }
            return targetPath === pathname;
        },
        [path, pathname, i18n.language]
    );

    const activeItem = useMemo(
        () =>
            menuItems.find((item, index) => isLinkActive(item.path, index) || isLinkActive(item.secondPath, index)) ??
            menuItems[0],
        [menuItems, isLinkActive]
    );

    return {menuItems, isLinkActive, activeItem, citySlug, pathname, i18n};
};

interface IMobilePlatformNavContext {
    isOpen: boolean;
    toggle: () => void;
    close: () => void;
}

const MobilePlatformNavContext = createContext<IMobilePlatformNavContext | null>(null);

const useMobilePlatformNav = () => {
    const context = useContext(MobilePlatformNavContext);
    if (!context) {
        throw new Error("useMobilePlatformNav must be used within MobilePlatformNavProvider");
    }
    return context;
};

export const MobilePlatformNavProvider = ({children}: {children: ReactNode}) => {
    const [isOpen, setIsOpen] = useState(false);
    const toggle = useCallback(() => setIsOpen((open) => !open), []);
    const close = useCallback(() => setIsOpen(false), []);

    const value = useMemo(() => ({isOpen, toggle, close}), [isOpen, toggle, close]);

    return (
        <MobilePlatformNavContext.Provider value={value}>
            {children}
            <MobilePlatformNavPanel />
        </MobilePlatformNavContext.Provider>
    );
};

export const MobilePlatformNavTrigger = () => {
    const {isOpen, toggle} = useMobilePlatformNav();
    const {activeItem} = usePlatformMenuNavigation();

    return (
        <button
            type="button"
            css={trigger}
            aria-label={isOpen ? "Zamknij menu" : activeItem.name}
            aria-expanded={isOpen}
            onClick={toggle}
        >
            {isOpen ? <CloseIcon size="2" fill="#fff" /> : getSideMenuIcon(activeItem.icon, "#fff", "2")}
        </button>
    );
};

const MobilePlatformNavPanel = () => {
    const {isOpen, close} = useMobilePlatformNav();
    const theme = useTheme();
    const history = useHistory();
    const {t, i18n} = useTranslation();
    const dispatch = useAppDispatch();
    const {menuItems, isLinkActive, citySlug, pathname} = usePlatformMenuNavigation();
    const shouldRouteToPlatformHome = useConditionalPlatformRoute(pathname);

    const handleMenuItemClick = (menuPath: string, name: string) => {
        history.push(menuPath);
        close();
        setTimeout(() => window.scrollTo(0, 0), 100);

        dataLayer({
            event: "left_menu_click",
            button: name,
            city: citySlug
        });
    };

    if (!isOpen) {
        return null;
    }

    return (
        <>
            <StyledOverlay onClick={close} />

            <StyledPanel>
                <nav css={panelNav}>
                    {menuItems.map(({name, path: menuPath, secondPath, icon}, i) => {
                        const isActive = isLinkActive(menuPath, i) || isLinkActive(secondPath, i);
                        const iconColor = isActive ? theme.colors.secondary : "#fff";

                        return (
                            <button
                                key={name}
                                type="button"
                                css={menuItem(isActive)}
                                title={name}
                                onClick={() => handleMenuItemClick(menuPath, name)}
                            >
                                <span css={menuItemIcon}>{getSideMenuIcon(icon, iconColor)}</span>
                                <Text css={menuItemLabel} variant="body_copy_2">
                                    {name}
                                </Text>
                            </button>
                        );
                    })}
                </nav>

                <div css={panelFooter}>
                    <button
                        type="button"
                        css={onboarding}
                        onClick={() => {
                            if (shouldRouteToPlatformHome) {
                                history.push(chartsRoutes[i18n.language].properties.root);
                            }
                            close();
                            dispatch(toggleOnboarding(true));
                        }}
                    >
                        <span css={menuItemIcon}>
                            <InfoIcon size="2.4" fill="#fff" />
                        </span>
                        <Text variant="body_copy_2">{t("onboarding.trigger")}</Text>
                    </button>
                </div>
            </StyledPanel>
        </>
    );
};

const trigger = (theme: Theme) => css`
    display: inline-flex;
    ${flexAlignCenter};
    justify-content: center;
    flex-shrink: 0;
    width: 3.6rem;
    height: 3.6rem;
    border-radius: 50%;
    border: none;
    cursor: pointer;
    background-color: ${theme.colors.secondary};

    &:focus-visible {
        outline: 2px solid ${theme.colors.primary};
        outline-offset: 2px;
    }

    ${onDesktop(css`
        display: none;
    `)};
`;

const StyledOverlay = styled.div`
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.5);
    ${zIndex(96)};

    @media (min-width: ${breakpoints.md}) {
        display: none;
    }
`;

const StyledPanel = styled.aside`
    position: fixed;
    top: 0;
    left: 0;
    ${zIndex(97)};
    display: flex;
    flex-direction: column;
    width: min(32rem, 100vw);
    max-width: 100vw;
    height: 100vh;
    background-color: ${(props) => props.theme.colors.secondary};
    overflow-y: auto;
    overflow-x: hidden;

    @media (min-width: ${breakpoints.md}) {
        display: none;
    }
`;

const panelNav = css`
    flex: 1;
    ${p(2, 1.5, 1)};
    padding-top: calc(var(--mobile-header-height) + 1rem);
`;

const menuItem = (isActive: boolean) => (theme: Theme) => css`
    display: flex;
    align-items: center;
    gap: 1.2rem;
    width: 100%;
    max-width: 100%;
    height: 4.5rem;
    min-height: 4.5rem;
    box-sizing: border-box;
    ${p(0, 1)};
    border: none;
    border-radius: 0.4rem;
    cursor: pointer;
    text-align: left;
    background: ${isActive ? theme.colors.gray[100] : "transparent"};
    color: ${isActive ? theme.colors.secondary : "#fff"};
    overflow: hidden;
`;

const menuItemIcon = css`
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 2.4rem;
    height: 2.4rem;
`;

const menuItemLabel = css`
    min-width: 0;
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1;
    text-align: left;
    color: inherit;
`;

const panelFooter = css`
    ${p(1.5, 1.5, 2)};
    border-top: 1px solid rgba(255, 255, 255, 0.12);
`;

const onboarding = (theme: Theme) => css`
    ${flexAlignCenter};
    justify-content: flex-start;
    gap: 1.2rem;
    width: 100%;
    height: 4.5rem;
    ${p(0, 1)};
    border: none;
    border-radius: 0.4rem;
    background: transparent;
    color: #fff;
    cursor: pointer;

    & > span {
        color: #fff;
    }

    &:hover {
        background: ${theme.colors.primary};
    }
`;
